<template>
  <el-dialog v-model="dialogVisible" title="开班" width="520px" append-to-body destroy-on-close :close-on-click-modal="!submitting" :close-on-press-escape="!submitting" :show-close="!submitting" @close="handleClose">
    <el-alert v-if="error" :title="error" type="warning" :closable="false" show-icon class="mb-16px" />
    <el-alert v-if="pending" title="有一笔结果待确认的开班请求，请先核对。将按上次填写的内容重试。" type="info" :closable="false" class="mb-16px" />
    <el-form label-width="140px" :disabled="submitting || Boolean(pending) || Boolean(sessionError)">
      <el-form-item label="开班员工">{{ userStore.getUserInfo()?.name || "--" }}</el-form-item>
      <el-form-item label="统计起点">
        <div>
          <el-checkbox v-model="carryPrevious" :disabled="loading || !preview?.carryAllowed">从上个交班时间开始</el-checkbox>
          <div class="text-sm text-[var(--el-text-color-secondary)]">{{ carryPrevious ? preview?.previousEndTime : "从实际开班时刻开始，之前的空档保留为未归属" }}</div>
          <div v-if="!preview?.carryAllowed && preview?.unavailableReason" class="text-sm">{{ preview.unavailableReason }}</div>
        </div>
      </el-form-item>
      <el-form-item label="上班留存现金">{{ money(preview?.previousCash) }}</el-form-item>
      <el-form-item label="空档净现金">{{ money(preview?.gapCash) }}</el-form-item>
      <el-form-item label="预计应有现金">{{ money(preview?.expectedCash) }}</el-form-item>
      <el-form-item label="备用金变动（元）">
        <div>
          <el-input-number v-model="movementYuan" :precision="2" :step="100" controls-position="right" />
          <div class="text-sm">正数投入，负数取出；不计营业收入，实点须包含本次变动。</div>
        </div>
      </el-form-item>
      <el-form-item label="实际点验现金（元）" required>
        <el-input-number v-model="countedYuan" :min="0" :precision="2" :step="100" controls-position="right" />
      </el-form-item>
      <el-form-item label="点验差额">{{ money(difference) }}</el-form-item>
      <el-form-item label="现金说明" :required="needsNote">
        <el-input v-model="cashNote" type="textarea" :rows="2" maxlength="500" show-word-limit placeholder="有差额或备用金变动时，请填写原因" />
      </el-form-item>
    </el-form>
    <div class="text-sm text-[var(--el-text-color-secondary)]">现金预览截至 {{ preview?.checkedAt || "尚未取得" }}，提交时按服务端最新流水核对。</div>
    <template #footer>
      <el-button :disabled="submitting || Boolean(pending)" :loading="loading" @click="loadPreview">刷新现金预览</el-button>
      <el-button type="primary" :disabled="Boolean(sessionError) || loading" :loading="submitting" @click="handleOpen">{{ pending ? "核对上次开班结果" : "确认开班" }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import type { ShiftOpeningPreview, ShiftOpeningRequest } from "@/api/user/shiftOpening"
import { ElMessage } from "element-plus"
import { v4 as uuidv4 } from "uuid"
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { previewShiftOpening, submitShiftOpening } from "@/api/user/shiftOpening"
import { applyShiftOpenResult } from "@/store/modules/shiftStatus"
import { useUserStore } from "@/store/modules/user"
import { getBusinessContextRevision } from "@/utils/businessContext/revision"
import { createOpeningSession, openingCashDifference, OpeningRejectedError, openingScopeKey } from "./shiftOpeningSession"

const props = defineProps<{ show: boolean; showClose?: boolean }>()
const emit = defineEmits(["update:show", "refresh"])
const userStore = useUserStore()
const scope = computed(() => ({ shopId: Number(userStore.shopId), userId: Number(userStore.getUserInfo()?.merchantUserId) }))
const scopeKey = computed(() => openingScopeKey(scope.value))
const dialogVisible = ref(false)
const preview = ref<ShiftOpeningPreview>()
const carryPrevious = ref(false)
const countedYuan = ref<number | undefined>(0)
const movementYuan = ref<number | undefined>(0)
const cashNote = ref("")
const pending = ref<ShiftOpeningRequest | null>(null)
const error = ref("")
const sessionError = ref("")
const loading = ref(false)
const submitting = ref(false)
let sequence = 0
let session: ReturnType<typeof createOpeningSession> | undefined
const cents = (value: number | undefined) => Math.round(Number(value) * 100)
const difference = computed(() => openingCashDifference(cents(countedYuan.value), preview.value?.expectedCash, cents(movementYuan.value)))
const needsNote = computed(() => cents(movementYuan.value) !== 0 || (difference.value !== null && difference.value !== 0))
const money = (value: number | null | undefined) => value == null || !Number.isFinite(value) ? "未核实" : `¥${(value / 100).toFixed(2)}`

async function loadPreview() {
  const seq = ++sequence
  const key = scopeKey.value
  const revision = getBusinessContextRevision()
  loading.value = true
  preview.value = undefined
  try {
    const response = await previewShiftOpening()
    if (seq !== sequence || key !== scopeKey.value || revision !== getBusinessContextRevision()) return
    if (response.code !== 0 || response.data?.shopId !== scope.value.shopId) throw new Error(response.msg || "现金预览不可用")
    preview.value = response.data
    if (!pending.value && !response.data.carryAllowed) carryPrevious.value = false
  } catch (cause) {
    if (seq !== sequence || key !== scopeKey.value || revision !== getBusinessContextRevision()) return
    if (!pending.value) carryPrevious.value = false
    error.value = `${cause instanceof Error ? cause.message : "现金预览失败"}；可从现在普通开班，现金历史基准将由服务端核对。`
  } finally {
    if (seq === sequence) loading.value = false
  }
}

watch([() => props.show, scopeKey], ([show]) => {
  sequence += 1
  session = undefined
  dialogVisible.value = show
  if (!show) return
  carryPrevious.value = false
  countedYuan.value = 0
  movementYuan.value = 0
  cashNote.value = ""
  error.value = ""
  sessionError.value = ""
  pending.value = null
  preview.value = undefined
  submitting.value = false
  try {
    session = createOpeningSession(window.localStorage, scope.value)
    pending.value = session.pending()
    if (pending.value) {
      carryPrevious.value = pending.value.carryPrevious
      countedYuan.value = pending.value.countedCash / 100
      movementYuan.value = pending.value.cashMovement / 100
      cashNote.value = pending.value.cashNote || ""
    }
  } catch (cause) {
    session = undefined
    sessionError.value = cause instanceof Error ? cause.message : "无法保存开班请求"
    error.value = sessionError.value
  }
  loadPreview()
}, { immediate: true })

function handleClose() {
  sequence += 1
  session = undefined
  emit("update:show", false)
}

onBeforeUnmount(() => {
  sequence += 1
  session = undefined
})

async function handleOpen() {
  if (submitting.value || !session) return
  const countedCash = cents(countedYuan.value)
  const cashMovement = cents(movementYuan.value)
  if (!pending.value && (!Number.isSafeInteger(countedCash) || countedCash < 0 || !Number.isSafeInteger(cashMovement))) {
    ElMessage.warning("请填写有效的现金金额")
    return
  }
  if (!pending.value && needsNote.value && !cashNote.value.trim()) {
    ElMessage.warning("请填写现金差额或备用金变动说明")
    return
  }
  const currentSession = session
  const key = scopeKey.value
  const targetShopId = scope.value.shopId
  const revision = getBusinessContextRevision()
  const payload: ShiftOpeningRequest = pending.value ?? {
    requestId: uuidv4(),
    carryPrevious: carryPrevious.value,
    countedCash,
    cashMovement,
    cashNote: cashNote.value.trim(),
    ...(carryPrevious.value ? { previousRecordId: preview.value?.previousRecordId, previousVersion: preview.value?.previousVersion } : {}),
  }
  submitting.value = true
  try {
    const result = await currentSession.submit(payload, submitShiftOpening)
    if (currentSession !== session || key !== scopeKey.value || revision !== getBusinessContextRevision()) return
    if (result.status === 1) {
      applyShiftOpenResult({
        targetShopId,
        currentShopId: Number(userStore.shopId),
        isOpen: true,
        snapshot: userStore.shiftStatusSnapshot,
        setOpen: (isOpen, shopId) => userStore.setOpen(isOpen, shopId),
        resetShiftStatus: () => userStore.resetShiftStatus(),
      })
      ElMessage.success("开班成功")
    } else {
      userStore.resetShiftStatus()
      ElMessage.info("已核对：这笔请求对应的班次已结束，请查看当前班次")
    }
    pending.value = null
    dialogVisible.value = false
    emit("update:show", false)
    emit("refresh")
  } catch (cause) {
    if (currentSession !== session || key !== scopeKey.value || revision !== getBusinessContextRevision()) return
    error.value = cause instanceof OpeningRejectedError ? cause.message : "开班结果尚未确认，请点击核对按钮按原请求重试。"
    if (cause instanceof OpeningRejectedError) loadPreview()
  } finally {
    if (currentSession === session) {
      submitting.value = false
      try {
        pending.value = currentSession.pending()
      } catch {
        sessionError.value = "无法读取开班请求记录，请恢复浏览器存储后重新打开核对"
        error.value = sessionError.value
      }
    }
  }
}
</script>
